import { useEffect, useState } from "react";
import { apiStaff } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Repeat, Pause, Play, Zap, Pencil, Trash2 } from "lucide-react";
import { PageHeader, Card, Table, Tr, Td, StatusPill, EmptyState, Loading } from "@/components/admin";

const APPLIANCES = ["washer","dryer","refrigerator","oven","stove","dishwasher","microwave","disposal","ice-maker","other"];
const FREQUENCIES = [
  { value: "quarterly", label: "Every 3 months" },
  { value: "semiannual", label: "Every 6 months" },
  { value: "yearly", label: "Every 12 months" },
];
const TIMES = ["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"];
const freqLabel = (v) => FREQUENCIES.find((f) => f.value === v)?.label || v;

export default function RecurringPlans() {
  const [plans, setPlans] = useState(null);
  const [techs, setTechs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = () => apiStaff.get("/crm/recurring").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  useEffect(() => {
    reload();
    apiStaff.get("/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, []);
  useRealtime("staff", ["recurring."], reload);

  async function toggleActive(p) {
    try {
      await apiStaff.patch(`/crm/recurring/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Plan paused" : "Plan resumed");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function generateNow(p) {
    try {
      await apiStaff.post(`/crm/recurring/${p.id}/generate-now`);
      toast.success(`Job created for ${p.customer_name} — next visit advanced`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function del(p) {
    if (!window.confirm(`Delete recurring plan for ${p.customer_name}? Already-created jobs are kept.`)) return;
    try {
      await apiStaff.delete(`/crm/recurring/${p.id}`);
      toast.success("Plan deleted");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  function openNew() { setEditing(null); setDialogOpen(true); }
  function openEdit(p) { setEditing(p); setDialogOpen(true); }

  if (plans === null) return <Loading />;
  const activeCount = plans.filter((p) => p.active).length;

  return (
    <>
      <PageHeader
        title="Recurring plans"
        subtitle={`${activeCount} active of ${plans.length} · jobs auto-created ${7} days before each visit`}
        actions={
          <Button data-testid="recurring-new-btn" onClick={openNew} className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-9">
            <Plus className="w-4 h-4 mr-1.5" /> New plan
          </Button>
        }
      />
      {plans.length === 0 ? (
        <Card>
          <EmptyState
            icon={Repeat}
            message="No recurring maintenance plans yet."
            hint="Create one and the scheduler will drop a job on the board 7 days before every visit."
            action={<Button data-testid="recurring-empty-new-btn" onClick={openNew} className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-9"><Plus className="w-4 h-4 mr-1.5" /> New plan</Button>}
          />
        </Card>
      ) : (
        <Card padded={false} className="overflow-x-auto">
          <Table
            head={[
              { label: "Customer" }, { label: "Phone" }, { label: "Appliance" },
              { label: "Frequency" }, { label: "Next visit" }, { label: "Tech" },
              { label: "Jobs", align: "right" }, { label: "Status" }, { label: "", align: "right" },
            ]}
            rows={plans.map((p) => (
              <Tr key={p.id} testid={`recurring-row-${p.id}`}>
                <Td>
                  <div className="font-medium">{p.customer_name}</div>
                  <div className="text-xs text-[#002060]/60 line-clamp-1">{p.address}</div>
                </Td>
                <Td className="num">{p.customer_phone}</Td>
                <Td className="capitalize">{p.appliance}</Td>
                <Td>{freqLabel(p.frequency)}</Td>
                <Td className="num" data-testid={`recurring-next-${p.id}`}>{p.next_run_date}{p.preferred_time ? ` · ${p.preferred_time}` : ""}</Td>
                <Td>{p.technician_name || <span className="text-[#002060]/50">Unassigned</span>}</Td>
                <Td align="right" className="num">{p.jobs_generated || 0}</Td>
                <Td><StatusPill status={p.active ? "scheduled" : "cancelled"} label={p.active ? "Active" : "Paused"} /></Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn testid={`recurring-generate-${p.id}`} title="Generate job now" onClick={() => generateNow(p)}><Zap className="w-3.5 h-3.5" /></IconBtn>
                    <IconBtn testid={`recurring-toggle-${p.id}`} title={p.active ? "Pause" : "Resume"} onClick={() => toggleActive(p)}>
                      {p.active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </IconBtn>
                    <IconBtn testid={`recurring-edit-${p.id}`} title="Edit" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                    <IconBtn testid={`recurring-delete-${p.id}`} title="Delete" onClick={() => del(p)} tone="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                  </div>
                </Td>
              </Tr>
            ))}
          />
        </Card>
      )}
      <PlanDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        techs={techs}
        onSaved={() => { setDialogOpen(false); reload(); }}
      />
    </>
  );
}

function IconBtn({ children, onClick, title, testid, tone = "default" }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`h-7 w-7 grid place-items-center rounded border transition-colors ${
        tone === "danger"
          ? "border-red-200 text-red-600 hover:bg-red-50"
          : "border-[#002060]/15 text-[#002060]/70 hover:bg-[#002060]/5"
      }`}
    >
      {children}
    </button>
  );
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const blank = () => ({
  customer_name: "", customer_phone: "", customer_email: "", address: "",
  appliance: "washer", notes: "", technician_id: "none",
  frequency: "quarterly", start_date: todayStr(), preferred_time: "09:00",
});

function PlanDialog({ open, onOpenChange, editing, techs, onSaved }) {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        customer_name: editing.customer_name || "",
        customer_phone: editing.customer_phone || "",
        customer_email: editing.customer_email || "",
        address: editing.address || "",
        appliance: editing.appliance || "washer",
        notes: editing.notes || "",
        technician_id: editing.technician_id || "none",
        frequency: editing.frequency || "quarterly",
        start_date: editing.next_run_date || editing.start_date || todayStr(),
        preferred_time: editing.preferred_time || "09:00",
      });
    } else {
      setForm(blank());
    }
  }, [open, editing]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setInput = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    if (!form.customer_name.trim() || !form.customer_phone.trim() || !form.address.trim()) {
      toast.error("Name, phone and address are required");
      return;
    }
    setBusy(true);
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim(),
      customer_email: form.customer_email.trim() || null,
      address: form.address.trim(),
      appliance: form.appliance,
      notes: form.notes.trim() || null,
      technician_id: form.technician_id === "none" ? null : form.technician_id,
      frequency: form.frequency,
      preferred_time: form.preferred_time,
    };
    try {
      if (editing) {
        await apiStaff.patch(`/crm/recurring/${editing.id}`, { ...payload, next_run_date: form.start_date });
        toast.success("Plan updated");
      } else {
        await apiStaff.post("/crm/recurring", { ...payload, customer_email: payload.customer_email || undefined, start_date: form.start_date });
        toast.success("Recurring plan created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-[#002060] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Repeat className="w-4 h-4 text-[#FF6600]" />{editing ? "Edit recurring plan" : "New recurring plan"}</DialogTitle>
          <DialogDescription className="text-[#002060]/70">
            A maintenance job is auto-created 7 days before each visit and lands on the dispatch board.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1 space-y-1">
            <Label>Customer name *</Label>
            <Input data-testid="recurring-form-name" value={form.customer_name} onChange={setInput("customer_name")} placeholder="Jane Smith" />
          </div>
          <div className="col-span-2 sm:col-span-1 space-y-1">
            <Label>Phone *</Label>
            <Input data-testid="recurring-form-phone" value={form.customer_phone} onChange={setInput("customer_phone")} placeholder="210-555-0100" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Email</Label>
            <Input data-testid="recurring-form-email" value={form.customer_email} onChange={setInput("customer_email")} placeholder="jane@example.com" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Address *</Label>
            <Input data-testid="recurring-form-address" value={form.address} onChange={setInput("address")} placeholder="123 Alamo St, San Antonio" />
          </div>
          <div className="col-span-2 sm:col-span-1 space-y-1">
            <Label>Appliance</Label>
            <Select value={form.appliance} onValueChange={set("appliance")}>
              <SelectTrigger data-testid="recurring-form-appliance"><SelectValue /></SelectTrigger>
              <SelectContent>
                {APPLIANCES.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-1 space-y-1">
            <Label>Frequency</Label>
            <Select value={form.frequency} onValueChange={set("frequency")}>
              <SelectTrigger data-testid="recurring-form-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-1 space-y-1">
            <Label>{editing ? "Next visit date" : "First visit date"}</Label>
            <Input data-testid="recurring-form-start" type="date" value={form.start_date} onChange={setInput("start_date")} />
          </div>
          <div className="col-span-2 sm:col-span-1 space-y-1">
            <Label>Preferred time</Label>
            <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
              <SelectTrigger data-testid="recurring-form-time"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Preferred technician</Label>
            <Select value={form.technician_id} onValueChange={set("technician_id")}>
              <SelectTrigger data-testid="recurring-form-tech"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Notes (shown as the job issue)</Label>
            <Textarea data-testid="recurring-form-notes" rows={2} value={form.notes} onChange={setInput("notes")} placeholder="Seasonal tune-up, clean condenser coils…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="recurring-form-save" disabled={busy} onClick={save} className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white">
            {busy ? "Saving…" : editing ? "Save changes" : "Create plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
